/**
 * FAQ body-honesty rail — a `FAQPage` node must describe copy the page RENDERS.
 *
 * ## The defect class
 *
 * `buildFaqPage` (`./schemaGraph.ts`) emits a `FAQPage` from any well-formed
 * `{q,a}` pair it finds in `.dcs/content.yaml`. That gate enforces the SHAPE of
 * the honesty rule (no invented Q&A) but it cannot enforce its SUBSTANCE: the
 * builder is pure and has no idea whether the page it decorates renders those
 * questions anywhere a human — or a crawler — can see them. Seeding
 * `pages.<slug>.faq` without also rendering a visible FAQ section therefore
 * ships structured data for content that does not exist on the page. Google's
 * FAQPage guidance requires the Q&A be visible to the user; markup for invisible
 * content is a spam signal, and it is the exact fabrication class the rest of
 * this module tree exists to prevent.
 *
 * Measured origin: the 2026-08-30 Iron Oak drafting pass
 * (`.docs/analysis/iron-oak-seo-drafts-2026-08-30.md` §3, "The honesty condition
 * on all of the above") — Iron Oak has Q&A-shaped copy and no FAQ section, so
 * applying the FAQPage finding as a one-step content seed would have produced
 * exactly this defect on a live customer site.
 *
 * ## The rail
 *
 * Given an emitted document, take every `Question.name` out of every `FAQPage`
 * JSON-LD node and require each one to appear in the PRERENDERED BODY TEXT.
 *
 * ## The vacuity trap this rail is built around
 *
 * The question text is, by construction, already present in the document — it is
 * sitting in the `<script type="application/ld+json">` block being audited.
 * `dcsSeoPlugin` bakes that block into `<head>`, which `extractPrerenderedBodyText`
 * never reads, but not every emitter does (vite-ssg and hand-rolled prerenders
 * put JSON-LD in the body), and a body extractor that keeps `<script>` content
 * would then find every question in every document — green against a site with
 * no FAQ section at all. `extractPrerenderedBodyText` therefore strips
 * `<script>`, `<style>`, `<noscript>` and comments CONTENT-AND-ALL, and
 * `faqBodyHonesty.test.ts` exercises the body-placed case explicitly.
 */

import { decodeHtmlEntities } from './headHonesty'
import type { HonestyMode } from './headHonesty'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** One emitted document to audit. */
export interface FaqBodyObservation {
  /** Route path as it appears in `pages.yaml` (e.g. `/` or `/services`). */
  route: string
  /** The emitted HTML for that route, exactly as written to `dist/`. */
  html: string
}

/** A `FAQPage` question with no counterpart in the rendered body. */
export interface FaqBodyViolation {
  route: string
  /** The `Question.name` verbatim, as authored in the JSON-LD. */
  question: string
  /** How many questions that document's FAQPage node(s) declared in total. */
  questionCount: number
}

/** Configuration for the rail. */
export interface FaqBodyHonestyOptions {
  /**
   * Route paths exempted from the comparison. An exemption is still the caller's
   * to LOG — same contract as `HeadHonestyOptions.allow`.
   */
  allow?: string[]
}

// -----------------------------------------------------------------------------
// Extraction
// -----------------------------------------------------------------------------

/**
 * Normalise a string for PRESENCE comparison (is this text on the page?), which
 * is a weaker question than head honesty's IDENTITY comparison and so normalises
 * more:
 *   - HTML entities decoded (`&amp;` ⇒ `&`) — the two sides are escaped
 *     differently: JSON-LD escapes for JSON, body copy escapes for HTML.
 *   - typographic look-alikes folded to ASCII (`’ ‘ “ ” — –` ⇒ `' ' " " - -`) —
 *     the schema string and the rendered string come from the SAME content key
 *     but the rendered one may pass through a typography transform. A rail that
 *     reds because a renderer curled an apostrophe is an accusation, not a find.
 *   - whitespace collapsed — rendered copy is wrapped and indented.
 *   - lowercased — a question rendered as a title-cased heading is still visible.
 *
 * Deliberately NOT normalised: words. Nothing is stemmed, dropped or fuzzy-
 * matched, so a "close enough" body text does not satisfy the rail.
 */
export function normalizeForPresence(value: string): string {
  return decodeHtmlEntities(value)
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFC')
    .toLowerCase()
}

/** Strip HTML comments content-and-all. */
function stripComments(html: string): string {
  return html.replace(/<!--[\s\S]*?-->/g, ' ')
}

/**
 * Every `<script type="application/ld+json">` payload in the document.
 *
 * Comments are stripped FIRST, and that is load-bearing twice over. A
 * commented-out JSON-LD block is not emitted schema and must not be audited as
 * if it were. More sharply: a comment that merely MENTIONS the string
 * `<script type="application/ld+json">` (documentation about the markup, which
 * both fixtures carry) gives the scanner a bogus opening tag, and the scan then
 * runs to the first REAL `</script>` — swallowing the genuine block into an
 * unparseable payload and silently reporting a page as having no FAQPage at
 * all. Measured while building this rail: without this line the planted-defect
 * fixture went GREEN.
 */
function jsonLdBlocks(html: string): string[] {
  const out: string[] = []
  const re = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi
  let m: RegExpExecArray | null
  const source = stripComments(html)
  while ((m = re.exec(source))) {
    if (!/type\s*=\s*["']?application\/ld\+json/i.test(m[1])) continue
    out.push(m[2])
  }
  return out
}

/** Walk a parsed JSON-LD payload (object, array, or `@graph`) node by node. */
function walkNodes(value: unknown, visit: (node: Record<string, unknown>) => void): void {
  if (Array.isArray(value)) {
    for (const item of value) walkNodes(item, visit)
    return
  }
  if (!value || typeof value !== 'object') return
  const node = value as Record<string, unknown>
  visit(node)
  if ('@graph' in node) walkNodes(node['@graph'], visit)
}

function typesOf(node: Record<string, unknown>): string[] {
  const t = node['@type']
  if (typeof t === 'string') return [t]
  if (Array.isArray(t)) return t.filter((x): x is string => typeof x === 'string')
  return []
}

/**
 * Collect every `Question.name` declared by every `FAQPage` node in a document.
 *
 * Unparseable JSON-LD is SKIPPED, not reported — `cli/seo-coverage-audit.mjs`
 * already fails a route for unparseable structured data, and this rail must not
 * double-report someone else's finding as an FAQ defect.
 */
export function extractFaqQuestions(html: string): string[] {
  const questions: string[] = []
  for (const raw of jsonLdBlocks(html)) {
    let parsed: unknown
    try {
      parsed = JSON.parse(decodeHtmlEntities(raw.trim()))
    } catch {
      continue
    }
    walkNodes(parsed, (node) => {
      if (!typesOf(node).includes('FAQPage')) return
      const main = node.mainEntity
      const entries = Array.isArray(main) ? main : main ? [main] : []
      for (const entry of entries) {
        if (!entry || typeof entry !== 'object') continue
        const name = (entry as Record<string, unknown>).name
        if (typeof name === 'string' && name.trim()) questions.push(name.trim())
      }
    })
  }
  return questions
}

/**
 * The visible text of the PRERENDERED BODY — what a non-JS crawler receives.
 *
 * `<script>`, `<style>`, `<noscript>` and HTML comments are removed CONTENT AND
 * ALL before tags are stripped. That is not tidiness: the JSON-LD under audit
 * lives in a `<script>`, so leaving script content in would make every question
 * trivially "present" and the rail vacuous (see the module header).
 *
 * Returns `''` when there is no `<body>` — an SPA shell with no prerendered body
 * has no visible copy, which is the honest answer, not a parse failure.
 */
export function extractPrerenderedBodyText(html: string): string {
  const bodyMatch = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(html)
  if (!bodyMatch) return ''
  const stripped = stripComments(bodyMatch[1])
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
  return normalizeForPresence(stripped)
}

// -----------------------------------------------------------------------------
// The rail
// -----------------------------------------------------------------------------

/**
 * Report every `FAQPage` question that the route's prerendered body does not
 * render. A document with no `FAQPage` node yields nothing — the rail is about
 * schema that over-claims, never about pages that decline to have an FAQ.
 */
export function findFaqBodyHonestyViolations(
  observations: FaqBodyObservation[],
  options: FaqBodyHonestyOptions = {}
): FaqBodyViolation[] {
  const allowed = new Set(options.allow ?? [])
  const violations: FaqBodyViolation[] = []

  for (const obs of observations) {
    if (allowed.has(obs.route)) continue
    const questions = extractFaqQuestions(obs.html)
    if (questions.length === 0) continue

    const body = extractPrerenderedBodyText(obs.html)
    for (const question of questions) {
      if (body.includes(normalizeForPresence(question))) continue
      violations.push({ route: obs.route, question, questionCount: questions.length })
    }
  }

  return violations
}

/**
 * Render the full report. Names the route, the question, and the fix — both
 * halves of the fix, because deleting the schema and rendering the section are
 * both valid resolutions and the operator picks.
 */
export function formatFaqBodyHonestyReport(violations: FaqBodyViolation[]): string {
  const routes = new Set(violations.map((v) => v.route))
  const lines: string[] = [
    `[dcs-seo] FAQ body honesty: ${violations.length} FAQPage question(s) across ` +
      `${routes.size} route(s) are declared in JSON-LD but appear NOWHERE in the ` +
      `prerendered body — structured data for content no visitor and no crawler sees.`,
  ]
  for (const v of violations) {
    lines.push(`  ${v.route}  (${v.questionCount} question(s) declared)`)
    lines.push(`      not rendered: ${JSON.stringify(v.question)}`)
  }
  lines.push(
    `  Fix (pick one): render the same Q&A pairs as a visible FAQ section on the ` +
      `route, reading the SAME .dcs/content.yaml keys the schema is built from; ` +
      `or delete the faq entries so no FAQPage is emitted. Do NOT exempt the ` +
      `route — an exemption here asserts "this markup describes invisible copy ` +
      `on purpose", which is the spam signal itself.`
  )
  return lines.join('\n')
}

/** Thrown in `error` mode so the build goes red on invisible FAQ markup. */
export class FaqBodyHonestyError extends Error {
  readonly violations: FaqBodyViolation[]
  constructor(violations: FaqBodyViolation[]) {
    super(formatFaqBodyHonestyReport(violations))
    this.name = 'FaqBodyHonestyError'
    this.violations = violations
  }
}

/**
 * Enforce the rail. `error` throws, `warn` logs, `off` does nothing.
 * Mirrors `assertHeadHonesty` so the three-mode contract is uniform across rails.
 */
export function assertFaqBodyHonesty(
  observations: FaqBodyObservation[],
  options: FaqBodyHonestyOptions & { mode?: HonestyMode } = {}
): FaqBodyViolation[] {
  const { mode = 'error', ...rest } = options
  if (mode === 'off') return []
  const violations = findFaqBodyHonestyViolations(observations, rest)
  if (violations.length === 0) return []
  if (mode === 'warn') {
    console.warn(formatFaqBodyHonestyReport(violations))
    return violations
  }
  throw new FaqBodyHonestyError(violations)
}
